import { authenticator } from 'libs/additiv-react-oidc-client/core/authenticator';
import { setup } from 'additiv-services';
import { TokenTypes } from 'constants/auth-constants';
import { INIT_LOCATION } from '../constants/sessionStorageKeys';

// A single 401 is not proof the session is over — it is usually a token that expired between being
// read and the request reaching the API. Try one silent renewal before logging the user out.
const RENEW_COOLDOWN_MS = 60000;

let renewInFlight: Promise<boolean> | null = null;
let renewSucceededAt = 0;
let renewFailedAt = 0;

const applyRenewedToken = (accessToken: string) => {
    setup({
        member_token: `${accessToken}`,
        bearerAccessToken: `Bearer ${accessToken}`,
        bearerApplicationAccessToken: `Bearer ${accessToken}`,
    });
    sessionStorage.setItem(TokenTypes.member_token, `${accessToken}`);
};

const tryRenewSession = (): Promise<boolean> => {
    const now = Date.now();

    // Requests that were already in flight with the old token keep returning 401 for a moment after
    // a successful renewal. Those must not end the session.
    if (renewSucceededAt && now - renewSucceededAt < RENEW_COOLDOWN_MS) {
        return Promise.resolve(true);
    }

    // A renewal in progress: wait for it rather than starting a second one.
    if (renewInFlight) { return renewInFlight; }

    // Renewal already failed recently and 401s continue — the session really is gone.
    if (renewFailedAt && now - renewFailedAt < RENEW_COOLDOWN_MS) {
        return Promise.resolve(false);
    }

    renewInFlight = authenticator.signinSilent()
        .then((user) => {
            if (!user || user.expired || !user.access_token) {
                renewFailedAt = Date.now();

                return false;
            }

            applyRenewedToken(user.access_token);
            renewSucceededAt = Date.now();

            return true;
        })
        .catch(() => {
            renewFailedAt = Date.now();

            return false;
        })
        .finally(() => { renewInFlight = null; });

    return renewInFlight;
};

export const checkStatus = (status: number) => {
    if (status === 401) {
        tryRenewSession().then((renewed) => {
            if (renewed) { return; }

            sessionStorage.removeItem(INIT_LOCATION);
            authenticator.logout();
        });
    }
};
